import React, { RefObject, useCallback, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import cls from 'classnames';

import Autocomplete, { CustomOptionProps } from '@ott/autocomplete';
import AirportIcon from '@ott/icon-airport';
import BusIcon from '@ott/icon-bus';
import MetroIcon from '@ott/icon-metro';
import TrainIcon from '@ott/icon-train';
import { useLayoutContext } from '@ott/layout-context';
import MapPointIcon from '@ott/map-point-icon';

import { FIELDS, FORM_NAME, LABELS } from '../../constants';
import checkIfOptionMatchesValue from '../../helpers/checkIfOptionMatchesValue';
import concatenate from '../../helpers/concatenate';
import * as km from '../../statistic/events';
import {
  GeoAutocompleteOption,
  SearchForm,
  StationType,
  Suggestion,
  TargetType,
} from '../../types';
import InputLoaderIcon from './blocks/InputLoaderIcon/InputLoaderIcon';

import styles from './GeoAutocomplete.scss';

const SuggestionIcons = {
  [StationType.BusTerminal]: <BusIcon className={styles.optionIcon} />,
  [StationType.BusStation]: <BusIcon className={styles.optionIcon} />,
  [StationType.RailwayStation]: <TrainIcon className={styles.optionIcon} />,
  [StationType.Airport]: <AirportIcon className={styles.optionIcon} />,
  [StationType.MetroStation]: <MetroIcon className={styles.optionIcon} />,
  [StationType.Mall]: <MapPointIcon className={styles.optionIcon} />,
};

type Props = {
  abortInitialValuesSettingIfNeeded: () => void;
  areSuggestionsLoading: boolean;
  inputRef: RefObject<HTMLInputElement>;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  onSelect: () => void;
  options: Suggestion[];
  type: typeof FIELDS.DEPARTURE | typeof FIELDS.DESTINATION;
};

const GeoAutocomplete = ({
  abortInitialValuesSettingIfNeeded,
  areSuggestionsLoading,
  inputRef,
  isOpen,
  onClose,
  onOpen,
  onSelect,
  options,
  type,
}: Props) => {
  const { isMobile } = useLayoutContext();
  const [timestampWhenWasOpened, setTimestampWhenWasOpened] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTimestampWhenWasOpened(Date.now());
    }
  }, [isOpen]);

  const { control, getValues } = useFormContext<SearchForm>();
  const { field: optionField } = useController({ name: `${type}Option`, control });
  const {
    field: inputField,
    fieldState: { invalid },
  } = useController({
    name: type,
    control,
    rules: {
      required: `Укажите пункт ${type === FIELDS.DEPARTURE ? 'отправления' : 'прибытия'}`,
      validate: (inputFieldValue) => {
        const optionFieldValue = getValues(
          type === FIELDS.DEPARTURE ? FIELDS.DEPARTURE_OPTION : FIELDS.DESTINATION_OPTION
        );

        return (
          (inputFieldValue && inputFieldValue === optionFieldValue?.label) ||
          `Не удалось найти пункт ${type === FIELDS.DEPARTURE ? 'отправления' : 'прибытия'}`
        );
      },
    },
  });

  const renderOption = useCallback(
    ({ option, isHovered }: CustomOptionProps<GeoAutocompleteOption>) => {
      const { entityType, label, region, district, country, geoPoint, targetType } = option;

      const icon = (entityType && SuggestionIcons[entityType]) || (
        <MapPointIcon className={styles.optionIcon} />
      );

      return (
        <div
          data-locator={`autocomplete-option-id-${option.value}-${option.targetType}`}
          className={cls(styles.option, {
            [styles.isOptionHovered]: isHovered,
          })}
        >
          <div>{icon}</div>
          <div className={styles.optionInfoBlock}>
            <div className={styles.label}>{label}</div>
            <div className={styles.country}>
              {concatenate(
                targetType === TargetType.Station ? geoPoint.name : null,
                district,
                region,
                country
              )}
            </div>
          </div>
        </div>
      );
    },
    []
  );

  const handleSelect = (
    value: GeoAutocompleteOption['value'] | null,
    option: GeoAutocompleteOption | null
  ) => {
    optionField.onChange(option);
    onSelect();

    km.trackSuggestionSelect({
      suggestions: options,
      timestampWhenWasOpened,
      direction: type,
      input: inputField.value,
      option,
    });
  };

  const handleClose = () => {
    onClose();

    // Если стерли инпут и закрыли оверлей, кликнув вникуда, то в инпут вернется значение текущей выбранной опции
    if (!inputField.value && optionField.value) {
      inputField.onChange(optionField.value.label);
    }
  };

  const handleFocus = () => {
    inputRef.current?.select();
    abortInitialValuesSettingIfNeeded();
    onOpen();
  };

  const mask = (() => {
    if (!inputField.value) {
      return '';
    }

    if (optionField.value && inputField.value === optionField.value?.label) {
      return concatenate(
        optionField.value.label,
        optionField.value.district,
        optionField.value.region,
        optionField.value.country
      );
    }

    const potentialOption = options.find((option) =>
      checkIfOptionMatchesValue(option.label, inputField.value)
    ) as GeoAutocompleteOption;

    if (potentialOption) {
      return concatenate(
        potentialOption.label,
        potentialOption.district,
        potentialOption.region,
        potentialOption.country
      );
    }

    return '';
  })();

  const inputLoader = (
    <div className={styles.inputLoaderIcon}>
      <InputLoaderIcon />
    </div>
  );

  return (
    <Autocomplete
      CustomOptionRenderer={renderOption}
      autoComplete="off"
      form={FORM_NAME}
      fullscreenOnMobile={true}
      inputValue={inputField.value}
      isInvalid={isMobile && invalid}
      label={LABELS[type]}
      mask={areSuggestionsLoading ? '' : mask}
      modalTitle={LABELS[type]}
      name={inputField.name}
      onChange={inputField.onChange}
      onClose={handleClose}
      onFocus={handleFocus}
      onSelect={handleSelect}
      // eslint-disable-next-line @typescript-eslint/no-shadow
      optionFilter={(options) => options}
      options={options}
      postfixNode={areSuggestionsLoading ? inputLoader : null}
      ref={inputRef}
      value={optionField.value?.value}
      withTitle={false}
      specificLocator={`${inputField.name}-autocomplete`}
      className={styles.autocomplete}
      childrenClassNames={{
        triggerChildrenClassNames: {
          input: styles.geoInput,
          icon: styles.triggerIcon,
        },
        trigger: cls({
          [styles.geoTrigger]: !isMobile || (isMobile && !isOpen),
          [styles.shouldDisableHover]: isOpen || areSuggestionsLoading,
          [styles.departure]: type === FIELDS.DEPARTURE,
          [styles.destination]: type === FIELDS.DESTINATION,
        }),
        select: { overlayContent: styles.geoOverlay },
      }}
    />
  );
};
export default React.memo(GeoAutocomplete);
